/**
 * Ticket Z13: Serverfunktionen der TTPA-Hinweisseite.
 *
 * Sichtbarkeit: Jede Funktion prüft zuerst serverseitig das Feature-Flag
 * `module_ttpa_info_visible`. Bei ausgeschaltetem Flag liefert die
 * Sichtbarkeitsabfrage `false` und alle Inhalts- und Schreibfunktionen brechen
 * mit „Forbidden" ab – unabhängig davon, was die Ansicht anzeigt.
 *
 * Rollentrennung wie in Z7a: Kundenfunktionen prüfen zusätzlich die aktive
 * Modulzuweisung, Pflegefunktionen verlangen Administratorenrechte, die
 * Interessentenliste ist für Administratoren und Editoren lesbar.
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { FEATURE_FLAGS } from "@/lib/feature-flags.keys";
import type {
  InterestRegistration,
  TtpaInfoPage,
  TtpaInfoPageAdmin,
} from "@/lib/module-ttpa";

type Ctx = { supabase: any; userId: string };

async function flagEnabled(): Promise<boolean> {
  const { isFeatureEnabled } = await import("@/lib/feature-flags.server");
  return isFeatureEnabled(FEATURE_FLAGS.moduleTtpaInfoVisible);
}

async function assertFlag(): Promise<void> {
  if (!(await flagEnabled())) throw new Error("Forbidden");
}

async function assertOwnModule(userId: string): Promise<void> {
  const { loadClientModulesForUser } = await import("@/lib/client-modules.server");
  const modules = await loadClientModulesForUser(userId);
  if (!modules.hundert_tage) throw new Error("Forbidden");
}

async function assertStaff(ctx: Ctx): Promise<void> {
  const { data: isAdmin } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (isAdmin) return;
  const { data: isEditor } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "editor",
  });
  if (!isEditor) throw new Error("Forbidden");
}

async function assertAdmin(ctx: Ctx): Promise<void> {
  const { data: isAdmin } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (!isAdmin) throw new Error("Forbidden");
}

// ============================================================
// KUNDENSICHT (Cockpit)
// ============================================================

/** Ist die Seite für dieses Konto sichtbar? Steuert Navigation und Umleitung. */
export const isTtpaPageVisible = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<boolean> => {
    if (!(await flagEnabled())) return false;
    const { loadClientModulesForUser } = await import("@/lib/client-modules.server");
    const modules = await loadClientModulesForUser(context.userId);
    return modules.hundert_tage;
  });

export const getMyTtpaPage = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<TtpaInfoPage | null> => {
    await assertFlag();
    await assertOwnModule(context.userId);
    const { loadOwnTtpaPage } = await import("@/lib/module-ttpa.server");
    return loadOwnTtpaPage(context.userId);
  });

export const registerEasyTtpaInterest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ registered: true }> => {
    await assertFlag();
    await assertOwnModule(context.userId);
    const { registerOwnInterest } = await import("@/lib/module-ttpa.server");
    return registerOwnInterest(context.userId);
  });

// ============================================================
// TEAMSICHT (Admin)
// ============================================================

export const getTtpaPageForStaff = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<TtpaInfoPageAdmin | null> => {
    await assertStaff(context as Ctx);
    const { loadTtpaPageForStaff } = await import("@/lib/module-ttpa.server");
    return loadTtpaPageForStaff();
  });

export const saveTtpaInfoPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        title: z.string().trim().min(1).max(200),
        body: z.string().max(50_000),
        internalNote: z.string().max(500).nullable(),
        isDraft: z.boolean(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }): Promise<TtpaInfoPageAdmin> => {
    await assertAdmin(context as Ctx);
    const { saveTtpaPage } = await import("@/lib/module-ttpa.server");
    return saveTtpaPage({ ...data, userId: context.userId });
  });

export const listEasyTtpaInterest = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<InterestRegistration[]> => {
    await assertStaff(context as Ctx);
    const { loadInterestList } = await import("@/lib/module-ttpa.server");
    return loadInterestList();
  });
